import styled from 'styled-components';
import { P, TABLET, fs, radius, card, eyebrow, ease, fontMono, onPhone, scrollRow, tint, tintB, tintT } from '../../theme/tokens';

export const TabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  ${onPhone} {
    gap: 8px;
    margin-bottom: 12px;
  }
`;

export const TabsActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  > :first-child {
    flex: 1;
    min-width: 0;
    margin: 0;
  }
  > button {
    border-radius: 999px;
    padding: 0 14px;
  }
`;

export const MsgLayout = styled.div`
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  height: calc(100vh - 232px);
  min-height: 520px;

  @media (max-width: ${TABLET}px) {
    grid-template-columns: minmax(0, 1fr);
    height: auto;
    min-height: 0;
    ${(p) => p.$detail && 'flex: 1; > :first-child { display: none; }'}
  }
  ${onPhone} {
    ${(p) => p.$detail && '> section { border: 0; border-radius: 0; box-shadow: none; }'}
  }
`;

export const Card = styled.section`
  ${card}
  overflow: hidden;
`;

export const ListTop = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: ${(p) => (p.$tall ? 40 : 36)}px;
  padding: 0 16px;
  border: none;
  border-radius: ${(p) => (p.$tall ? 10 : radius.md)}px;
  background: ${P.grad};
  color: ${P.white};
  font-family: inherit;
  font-size: ${fs.sm}px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 150ms ${ease};

  &:hover:not(:disabled) {
    background: ${P.gradHover};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  svg {
    font-size: 11px;
  }
`;

export const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${P.border};
  border-radius: ${radius.md}px;
  background: ${P.white};
  color: ${P.muted};
  font-family: inherit;
  font-size: ${fs.xs}px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 150ms, color 150ms;

  &:hover:not(:disabled) {
    border-color: ${P.navy};
    color: ${P.navyInk};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  svg {
    font-size: 11px;
  }
  ${onPhone} {
    width: 40px;
    height: 40px;
    padding: 0;
    justify-content: center;
    flex: none;
    svg {
      font-size: 14px;
    }
    span {
      display: none;
    }
  }
`;

export const BackBtn = styled(GhostBtn)`
  margin-left: -4px;
  border: none;
`;

export const Seg = styled.div`
  display: flex;
  gap: 4px;
  padding: 3px;
  background: ${P.bgSubtle};
  border: 1px solid ${P.line};
  border-radius: 10px;
`;

export const SegBtn = styled.button`
  flex: 1;
  height: 30px;
  border: none;
  border-radius: ${radius.md}px;
  font-family: inherit;
  font-size: ${fs.xs}px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$active ? P.white : 'transparent')};
  color: ${(p) => (p.$active ? P.navyInk : P.subtle)};
  box-shadow: ${(p) => (p.$active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};
  transition: background 150ms ${ease}, color 150ms ${ease};
`;

export const ListCard = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const ConvoScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border-top: 1px solid ${P.line};

  @media (max-width: ${TABLET}px) {
    max-height: 560px;
  }
  ${onPhone} {
    > button {
      grid-template-columns: 30px minmax(0, 1fr);
      gap: 9px;
      padding: 9px 12px;
      > span:first-child {
        width: 30px;
        height: 30px;
        margin-top: 1px;
        font-size: 11px;
      }
      > div > div:first-child > span:first-child {
        font-size: 13.5px;
      }
      > div > div:first-child > span:last-child {
        font-size: 11.5px;
      }
      > div > div:nth-child(2) {
        font-size: 12.5px;
        color: #434343;
      }
      > div > div:nth-child(3) {
        margin-top: 3px;
      }
    }
  }
`;

export const ListEmpty = styled.div`
  padding: 36px 16px;
  text-align: center;
  font-size: ${fs.sm}px;
  color: ${P.subtle};
`;

export const Thread = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const ThreadHead = styled.header`
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${P.line};
  ${onPhone} {
    flex-wrap: nowrap;
    gap: 8px;
    padding: 8px 12px;
    > [aria-hidden='true'] {
      display: none;
    }
  }
`;

export const ThreadName = styled.h2`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${P.ink};
  ${onPhone} {
    gap: 6px;
  }
`;

export const ThreadNameText = styled.span`
  ${onPhone} {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ThreadRoute = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: ${fs.xs}px;
  color: ${P.subtle};
  font-variant-numeric: tabular-nums;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
  }
  svg {
    font-size: 11px;
    color: ${P.muted};
  }
  ${onPhone} {
    flex-wrap: nowrap;
    gap: 10px;
    white-space: nowrap;
    span {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    span:last-child {
      flex: none;
    }
    svg {
      margin-right: 5px;
    }
  }
`;

export const MaggieOn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${fs.xs}px;
  font-weight: 700;
  color: ${P.purple};

  svg {
    font-size: 11px;
  }
  ${onPhone} {
    display: none;
  }
`;

export const ThreadStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${fs.xs}px;
  color: ${(p) => p.$color || P.muted};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const HeadActions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
  ${onPhone} {
    gap: 4px;
    > :not(${GhostBtn}) {
      display: none;
    }
  }
`;

export const History = styled.ol`
  list-style: none;
  margin: 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: ${P.bgSubtle};
  ${onPhone} {
    padding: 12px;
  }
`;

export const HistoryNote = styled.li`
  margin: auto;
  font-size: ${fs.sm}px;
  color: ${P.subtle};
`;

export const ThreadFoot = styled.div`
  flex-shrink: 0;
  padding: 14px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid ${P.line};
  ${onPhone} {
    gap: 6px;
    padding: 6px 10px 8px;
    background: ${P.white};
    box-shadow: 0 -4px 12px rgba(11, 20, 38, 0.06);
    textarea {
      min-height: 40px;
      height: 40px;
      padding: 9px 12px;
    }
    textarea + button {
      height: 40px;
    }
    > :last-child > :last-child {
      display: none;
    }
  }
`;

export const LinkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: ${fs.xs}px;
  font-weight: 600;
  color: ${P.navyInk};
  cursor: pointer;

  border-radius: ${radius.sm}px;

  &:hover:not(:disabled) {
    color: ${P.navy};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  &:focus-visible {
    outline: 2px solid ${tintB(P.info)};
    outline-offset: 3px;
  }
  &:disabled {
    color: ${P.faint};
    cursor: not-allowed;
  }
  svg {
    font-size: 11px;
  }
`;

export const EmptyThread = styled.div`
  display: grid;
  place-items: center;
  gap: 10px;
  height: 100%;
  min-height: 320px;
  text-align: center;
  color: ${P.subtle};
  font-size: ${fs.sm}px;

  svg {
    font-size: 26px;
    color: ${P.faint};
    margin-bottom: 10px;
  }
`;

export const Picker = styled.div`
  width: 380px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  max-height: 420px;
`;

export const PickerHead = styled.div`
  padding: 14px 16px 10px;
  border-bottom: 1px solid ${P.line};

  div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  h3 {
    ${eyebrow}
    margin: 0;
  }
`;

export const PickerList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 6px;
  overflow-y: auto;
`;

export const PickerItem = styled.button`
  width: 100%;
  display: grid;
  gap: 4px;
  padding: 10px 12px;
  border: none;
  border-radius: ${radius.md}px;
  background: none;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: background 120ms ${ease};

  &:hover,
  &:focus-visible {
    background: ${P.rowHover};
    outline: none;
  }
`;

export const PickerTitle = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: ${fs.sm}px;
  font-weight: 600;
  color: ${P.ink};
`;

export const PickerBody = styled.span`
  font-size: ${fs.xs}px;
  line-height: 1.5;
  color: ${P.muted};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const PickerEmpty = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: ${fs.sm}px;
  color: ${P.subtle};
`;

export const Var = styled.span`
  padding: 0 4px;
  border-radius: 4px;
  font-family: ${fontMono};
  font-size: 0.92em;
  background: ${tint(P.info)};
  color: ${tintT(P.info)};
  ${onPhone} {
    font-size: 1em;
  }
`;

export const TplHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${P.line};

  p {
    flex: 1 1 320px;
    min-width: 0;
    margin: 0;
    font-size: ${fs.sm}px;
    color: ${P.muted};
  }
  code {
    font-size: ${fs.xs}px;
    background: ${P.bgSubtle};
    border: 1px solid ${P.line};
    border-radius: 4px;
    padding: 1px 5px;
  }
`;

export const TplFilters = styled.div`
  padding: 14px 20px 4px;
`;

export const TplGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 14px 20px 20px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const TplItem = styled.li`
  display: flex;
  flex-direction: column;
  border: 1px solid ${P.border};
  border-radius: 10px;
  padding: 13px 15px;
  transition: border-color 150ms;

  &:hover {
    border-color: ${P.faint};
  }
`;

export const TplTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: ${fs.sm}px;
    font-weight: 700;
    color: ${P.ink};
  }
`;

export const Muted = styled.span`
  font-size: ${(p) => (p.$small ? 11 : fs.xs)}px;
  color: ${P.subtle};
  margin-left: ${(p) => (p.$push ? 'auto' : 0)};
  ${onPhone} {
    font-size: ${fs.xs}px;
  }
`;

export const TplBody = styled.p`
  flex: 1;
  margin: 8px 0 0;
  font-size: ${fs.xs}px;
  line-height: 1.55;
  color: ${P.muted};
  white-space: pre-wrap;
`;

export const TplFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 11px;
`;

export const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: ${radius.sm}px;
  background: none;
  color: ${P.subtle};
  cursor: pointer;

  &:hover {
    background: ${P.gray100};
    color: ${(p) => (p.$danger ? P.danger : P.navyInk)};
  }
  svg {
    font-size: 12px;
  }
`;

export const Cols2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Panel = styled.section`
  ${card}
  padding: 16px 20px 6px;
`;

export const PanelTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;

  h2 {
    margin: 0;
    font-size: ${fs.md}px;
    font-weight: 700;
    color: ${P.ink};
  }
  span {
    font-size: ${fs.sm}px;
    color: ${P.subtle};
  }
`;

export const ListRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 13px 0;
  border-bottom: 1px solid ${P.line};

  &:last-child {
    border-bottom: none;
  }
`;

export const RowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${fs.sm}px;
  font-weight: 600;
  color: ${P.ink};

  svg {
    font-size: 9px;
    color: ${P.subtle};
  }
`;

export const RowSub = styled.div`
  margin-top: 2px;
  font-size: ${fs.xs}px;
  color: ${P.subtle};
`;

export const RowValue = styled.div`
  text-align: right;
  white-space: nowrap;

  b {
    display: block;
    font-size: ${fs.sm}px;
    color: ${P.ink};
  }
`;

export const SettingsStack = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 900px;
`;

export const SetHead = styled.h2`
  margin: 0;
  padding: 14px 20px;
  border-bottom: 1px solid ${P.line};
  font-size: 15px;
  font-weight: 700;
  color: ${P.ink};
`;

export const SetRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: ${(p) => (p.$tight ? '14px 20px' : '16px 20px')};
  border-bottom: 1px solid ${P.line};

  &:last-child {
    border-bottom: none;
  }
  ${onPhone} {
    padding: 14px 16px;
    ${(p) => p.$tight && 'flex-direction: column; align-items: stretch; gap: 8px;'}
  }
  label {
    font-size: ${fs.sm}px;
    font-weight: 600;
    color: ${P.ink};
  }
`;

export const SetBlock = styled.div`
  padding: 16px 20px;
`;

export const SaveBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  span {
    font-size: ${fs.xs}px;
    color: ${P.subtle};
  }
`;

export const Intent = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 18px;
  background: ${(p) => (p.$ai ? '#F9F0FF' : '#EEF3FA')};
  color: ${(p) => (p.$ai ? '#531DAB' : '#1D3557')};
  border-bottom: 1px solid ${(p) => (p.$ai ? '#EADCFB' : '#DCE6F3')};

  b {
    color: inherit;
  }
  svg {
    color: inherit;
    font-size: 13px;
  }
`;

export const InboxTopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

export const ActionStrip = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 18px 0;
  ${onPhone} {
    padding: 6px 10px;
    background: #fffbf0;
    border-bottom: 1px solid ${P.line};
  }
`;

export const StripToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 0 10px;
  border: 1px solid #f3ddb0;
  border-radius: ${radius.md}px;
  background: ${P.white};
  font-family: inherit;
  font-size: ${fs.sm}px;
  color: #7a4a00;
  text-align: left;
  cursor: pointer;
  span {
    flex: 1;
  }
`;

export const ActionBanner = styled.div`
  border: 1px solid ${P.line};
  background: ${P.bgSubtle};
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  > svg {
    color: ${P.navy};
  }
`;

export const ActionBannerHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${fs.sm}px;
  font-weight: 600;
  color: ${P.navyInk};

  svg {
    color: ${P.navy};
    font-size: 12px;
  }
`;

export const ActionBannerSub = styled.div`
  font-size: ${fs.xs}px;
  color: ${P.subtle};
`;

export const ActionBannerBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const QuickRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  ${onPhone} {
    flex-wrap: nowrap;
    margin-bottom: 0;
    ${scrollRow}
    > * {
      flex: none;
    }
  }
`;

export const QuickLabel = styled.span`
  ${eyebrow}
  margin-right: 2px;
`;

export const ShowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ShowLabel = styled.span`
  ${eyebrow}
`;
